import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { GPA_LOGO } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

function BrandPanel() {
  return (
    <div className="brand-hero relative hidden w-1/2 flex-col justify-between p-12 text-white lg:flex">
      <div className="brand-mesh absolute inset-0" />
      <Link to="/" className="relative inline-flex items-center rounded-lg bg-white px-3 py-2">
        <img src={GPA_LOGO} alt="GPA+" className="h-9 w-auto" />
      </Link>
      <div className="relative">
        <h2 className="font-heading text-4xl font-extrabold leading-tight">The Transferable<br />Skills Benchmark</h2>
        <p className="mt-4 max-w-md text-slate-300">Reset your password to get back into your GPA+ account.</p>
      </div>
      <p className="relative text-xs text-slate-400">FERPA-aligned · Role-based access · No real student PII</p>
    </div>
  );
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <BrandPanel />
      <div className="flex w-full items-center justify-center bg-slate-50 px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-md">
          <Link to="/login" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#003057]">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>

          {sent ? (
            <div data-testid="forgot-sent">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#78be21]/15">
                <MailCheck className="h-6 w-6 text-[#5fa114]" />
              </div>
              <h1 className="font-heading text-3xl font-extrabold text-[#003057]">Check your email</h1>
              <p className="mt-2 text-slate-600">
                If an account exists for <span className="font-semibold text-[#003057]">{email}</span>, we've sent a
                secure link to reset your password. The link expires in 1 hour.
              </p>
              <p className="mt-4 text-sm text-slate-500">
                Didn't get it? Check spam, or{" "}
                <button type="button" onClick={() => setSent(false)} data-testid="forgot-retry"
                  className="font-semibold text-[#005291] hover:text-[#78be21]">try another email</button>.
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-extrabold text-[#003057]">Forgot password?</h1>
              <p className="mt-1 text-slate-500">Enter your account email and we'll send you a reset link.</p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="forgot-email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@lonestar.edu" className="mt-1.5" required />
                </div>
                {error && <p data-testid="forgot-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
                <Button type="submit" data-testid="forgot-submit" disabled={loading}
                  className="w-full rounded-full bg-[#003057] py-6 text-base font-bold hover:bg-[#004275]">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset link"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
